import { isFailure } from '@superbet-group/web.lib.utilities';
import { Store } from 'vuex';

import AuthenticationService from '@core/services/authentication/AuthenticationService';
import ScheduledUtil from '@core/utils/scheduled/ScheduledUtil';

import { ScheduledJob } from '../types';

type CheckLoginSessionWorker = {
    authenticationService: AuthenticationService;
    store: Store<unknown>;
    isAutoLogoutEnabled: boolean;
};

export function createSessionActivityJob(worker: () => void): ScheduledJob {
    let jobId: number | null = null;

    return {
        start() {
            if (jobId) {
                return;
            }
            jobId = ScheduledUtil.scheduleTask(worker, 60 * 1000);
            // call endpoint immediately after login
            worker();
            /**
             * This solves a scenario where user is on another tab at the moment of
             * sending a request. If user is on another tab every 60th second,
             * document.hidden will be true and request won't be sent. This listener
             * will be triggered when user switches back to Superbet tab and request
             * will be immediately sent. It multiple requests are sent in the interval
             * of one minute, BE will just accept the first one and ignore the rest.
             */
            document.addEventListener('visibilitychange', worker);
        },
        destroy() {
            document.removeEventListener('visibilitychange', worker);
            ScheduledUtil.removeScheduledTask(jobId!);
            jobId = null;
        },
    };
}

export function createCheckLoginSessionWorker({
    authenticationService,
    store,
    isAutoLogoutEnabled,
}: CheckLoginSessionWorker): () => Promise<void> {
    return async function checkLoginSession() {
        if (document.hidden) {
            return;
        }

        const session = await authenticationService.checkLoginSession(isAutoLogoutEnabled);

        if (isFailure(session) && session.shouldLogout) {
            store.dispatch('data/authentication/clearSessionState');
            if (isAutoLogoutEnabled) {
                store.dispatch('data/authentication/sessionExpired', session.error);
            }
        }
    };
}
